import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WorkflowActivity from '../components/workflow/WorkflowActivity';
import InsightCard from '../components/copilot/InsightCard';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Save, CheckCircle, Plus, Trash2, Edit } from 'lucide-react';
import axios from 'axios';
import DynamicFieldsSection from '../components/custom-fields/DynamicFieldsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const VendorForm = () => {
  const { vendorNumber } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!vendorNumber;

  const [activeTab, setActiveTab] = useState('general');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // General Data
  const [vendor, setVendor] = useState(null);
  const [accountGroups, setAccountGroups] = useState([]);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedAccountGroup, setSelectedAccountGroup] = useState('');
  const [numberRangeConfig, setNumberRangeConfig] = useState(null);
  const [isExternalNumbering, setIsExternalNumbering] = useState(false);
  const [isLoadingAccountGroupConfig, setIsLoadingAccountGroupConfig] = useState(false);

  // Company Codes
  const [companyCodes, setCompanyCodes] = useState([]);
  const [vendorCompanyCodes, setVendorCompanyCodes] = useState([]);
  const [isCompanyDialogOpen, setIsCompanyDialogOpen] = useState(false);

  // Purchasing
  const [purchasingOrgs, setPurchasingOrgs] = useState([]);
  const [purchasingGroups, setPurchasingGroups] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [vendorPurchasing, setVendorPurchasing] = useState([]);
  const [isPurchasingDialogOpen, setIsPurchasingDialogOpen] = useState(false);
  
  // Master Data from Customer Config
  const [termsOfPayment, setTermsOfPayment] = useState([]);
  const [incoterms, setIncoterms] = useState([]);

  // Partner Functions
  const [partnerFunctions, setPartnerFunctions] = useState([]); // Configured partner functions from account group
  const [allPartnerFunctions, setAllPartnerFunctions] = useState([]); // All partner functions for dropdown
  const [availableVendors, setAvailableVendors] = useState([]);
  const [vendorPartnerFunctions, setVendorPartnerFunctions] = useState([]);
  const [isPartnerDialogOpen, setIsPartnerDialogOpen] = useState(false);
  const [editingPartnerFunction, setEditingPartnerFunction] = useState(null);
  const [selectedPartnerFunctionId, setSelectedPartnerFunctionId] = useState('');

  // Custom fields (Frappe-style) — user-defined fields stored as custom_data
  const [customData, setCustomData] = useState({});

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (isEditMode && vendorNumber) {
      loadVendorData();
    }
  // eslint-disable-next-line
  }, [vendorNumber]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const [accountGroupsRes, countriesRes, languagesRes, companyCodesRes, purchOrgRes, purchGroupRes, currenciesRes, vendorsRes, partnerFuncsRes, termsOfPaymentRes, incotermsRes] = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/vendor-master/account-groups`),
        axios.get(`${BACKEND_URL}/api/master-data/countries`),
        axios.get(`${BACKEND_URL}/api/master-data/languages`),
        axios.get(`${BACKEND_URL}/api/company/company-codes`),
        axios.get(`${BACKEND_URL}/api/purchasing-management/purchasing-organizations`),
        axios.get(`${BACKEND_URL}/api/purchasing-management/purchasing-groups`),
        axios.get(`${BACKEND_URL}/api/master-data/currencies`),
        axios.get(`${BACKEND_URL}/api/vendor-master/vendors`),
        axios.get(`${BACKEND_URL}/api/vendor-master/partner-functions`),
        axios.get(`${BACKEND_URL}/api/customer-terms-of-payments`),
        axios.get(`${BACKEND_URL}/api/customer-incoterms`)
      ]);

      setAccountGroups(accountGroupsRes.status === 'fulfilled' ? accountGroupsRes.value.data : []);
      setCountries(countriesRes.status === 'fulfilled' ? countriesRes.value.data : []);
      setLanguages(languagesRes.status === 'fulfilled' ? languagesRes.value.data : []);
      setCompanyCodes(companyCodesRes.status === 'fulfilled' ? companyCodesRes.value.data : []);
      setPurchasingOrgs(purchOrgRes.status === 'fulfilled' ? purchOrgRes.value.data : []);
      setPurchasingGroups(purchGroupRes.status === 'fulfilled' ? purchGroupRes.value.data : []);
      setCurrencies(currenciesRes.status === 'fulfilled' ? currenciesRes.value.data : []);
      setAvailableVendors(vendorsRes.status === 'fulfilled' ? vendorsRes.value.data : []);
      setAllPartnerFunctions(partnerFuncsRes.status === 'fulfilled' ? partnerFuncsRes.value.data : []);
      setTermsOfPayment(termsOfPaymentRes.status === 'fulfilled' ? termsOfPaymentRes.value.data : []);
      setIncoterms(incotermsRes.status === 'fulfilled' ? incotermsRes.value.data : []);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load reference data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadVendorData = async () => {
    try {
      const [vendorRes, companyRes, purchasingRes, partnerRes] = await Promise.allSettled([
        axios.get(`${BACKEND_URL}/api/vendor-master/vendors/${vendorNumber}`),
        axios.get(`${BACKEND_URL}/api/vendor-master/vendor-company-codes?vendor_number=${vendorNumber}`),
        axios.get(`${BACKEND_URL}/api/vendor-master/vendor-purchasing-orgs?vendor_number=${vendorNumber}`),
        axios.get(`${BACKEND_URL}/api/vendor-master/vendor-partner-functions?vendor_number=${vendorNumber}`)
      ]);

      if (vendorRes.status === 'fulfilled') {
        setVendor(vendorRes.value.data);
        setSelectedAccountGroup(vendorRes.value.data.account_group_id);
        setCustomData(vendorRes.value.data.custom_data || {});
      }
      setVendorCompanyCodes(companyRes.status === 'fulfilled' ? companyRes.value.data : []);
      setVendorPurchasing(purchasingRes.status === 'fulfilled' ? purchasingRes.value.data : []);
      setVendorPartnerFunctions(partnerRes.status === 'fulfilled' ? partnerRes.value.data : []);
    } catch (error) {
      console.error('Error loading vendor data:', error);
      toast.error('Failed to load vendor data');
    }
  };

  const handleAccountGroupChange = async (accountGroupId) => {
    setSelectedAccountGroup(accountGroupId);
    
    if (!accountGroupId) {
      setNumberRangeConfig(null);
      setIsExternalNumbering(false);
      return;
    }

    // Set loading state to prevent premature error display
    setIsLoadingAccountGroupConfig(true);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vendor-master/account-group-configs/${accountGroupId}/vendor-creation-info`);
      
      // Only proceed if we have a valid response
      if (response.data) {
        // Check if number range is configured
        if (!response.data.number_range) {
          toast.error(`Number Range is not configured for Account Group "${accountGroupId}". Please configure it in the Config section.`, {
            duration: 5000
          });
          setNumberRangeConfig(null);
          setIsExternalNumbering(false);
          setPartnerFunctions([]);
        } else {
          // Successfully loaded - set the data
          setNumberRangeConfig(response.data.number_range);
          setIsExternalNumbering(response.data.number_range.range_type === 'EXTERNAL');
          setPartnerFunctions(response.data.partner_functions || []);
        }
      }
    } catch (error) {
      console.error('Account group config error:', error);
      
      // Check if account group config doesn't exist
      if (error.response?.status === 404) {
        toast.error(`Account Group "${accountGroupId}" is not configured. Please configure Number Range and Partner Functions in the Config section.`, {
          duration: 5000
        });
      } else {
        toast.error('Failed to load account group configuration. Please try again.');
      }
      
      setNumberRangeConfig(null);
      setIsExternalNumbering(false);
      setPartnerFunctions([]);
    } finally {
      setIsLoadingAccountGroupConfig(false);
    }
  };

  const handleSaveDraft = async (e) => {
    e.preventDefault();
    
    // Validate that number range is configured for the selected account group
    if (!numberRangeConfig && !isEditMode) {
      toast.error('Cannot create vendor. Number Range is not configured for the selected Account Group. Please configure it first.', {
        duration: 5000
      });
      return;
    }
    
    setIsSaving(true);

    const formData = new FormData(e.currentTarget);
    const data = {
      account_group_id: selectedAccountGroup,
      vendor_number: formData.get('vendor_number') || null,
      title: formData.get('title'),
      name: formData.get('name'),
      search_term: formData.get('search_term'),
      street: formData.get('street'),
      city: formData.get('city'),
      postal_code: formData.get('postal_code'),
      region: formData.get('region'),
      country_key: formData.get('country_key'),
      mobile_number: formData.get('mobile_number') || null,
      language_key: formData.get('language_key'),
      email: formData.get('email') || null,
      custom_data: customData,
      status: 'Draft'
    };

    try {
      if (isEditMode) {
        await axios.put(`${BACKEND_URL}/api/vendor-master/vendors/${vendorNumber}`, data);
        toast.success('Vendor updated successfully');
        loadVendorData();
      } else {
        const response = await axios.post(`${BACKEND_URL}/api/vendor-master/vendors`, data);
        toast.success('Vendor draft saved successfully');
        navigate(`/vendors/${response.data.vendor_number}`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMessages = detail.map(err => `${err.loc.join(' > ')}: ${err.msg}`).join('; ');
        toast.error(`Validation Error: ${errorMessages}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to save vendor');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!window.confirm('Are you sure you want to activate this vendor? This requires at least one company code and one purchasing organization.')) {
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/vendor-master/vendors/${vendorNumber}/activate`);
      toast.success('Vendor activated successfully');
      loadVendorData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate vendor');
    }
  };

  const handleAddCompanyCode = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const data = {
      vendor_number: vendorNumber,
      company_code_id: formData.get('company_code_id'),
      reconciliation_account: formData.get('reconciliation_account'),
      payment_terms: formData.get('payment_terms'),
      planning_group: formData.get('planning_group') || null
    };

    try {
      await axios.post(`${BACKEND_URL}/api/vendor-master/vendor-company-codes`, data);
      toast.success('Company code added successfully');
      setIsCompanyDialogOpen(false);
      loadVendorData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add company code');
    }
  };

  const handleDeleteCompanyCode = async (companyCodeId) => {
    if (!window.confirm('Are you sure you want to remove this company code assignment?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/vendor-master/vendor-company-codes/${vendorNumber}/${companyCodeId}`);
      toast.success('Company code removed successfully');
      loadVendorData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove company code');
    }
  };

  const handleAddPurchasing = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const data = {
      vendor_number: vendorNumber,
      purchasing_org_id: formData.get('purchasing_org_id'),
      order_currency: formData.get('order_currency'),
      purchasing_group_id: formData.get('purchasing_group_id'),
      incoterms: formData.get('incoterms') || null,
      payment_terms: formData.get('payment_terms_purchasing') || null
    };

    try {
      await axios.post(`${BACKEND_URL}/api/vendor-master/vendor-purchasing-orgs`, data);
      toast.success('Purchasing organization added successfully');
      setIsPurchasingDialogOpen(false);
      loadVendorData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add purchasing organization');
    }
  };

  const handleDeletePurchasing = async (purchasingOrgId) => {
    if (!window.confirm('Are you sure you want to remove this purchasing organization assignment?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/vendor-master/vendor-purchasing-orgs/${vendorNumber}/${purchasingOrgId}`);
      toast.success('Purchasing organization removed successfully');
      loadVendorData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove purchasing organization');
    }
  };

  const handleAddPartnerFunction = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const data = {
      vendor_number: vendorNumber,
      partner_function_id: formData.get('partner_function_id'),
      partner_number: formData.get('partner_number')
    };

    try {
      if (editingPartnerFunction) {
        // Update existing partner function
        await axios.put(
          `${BACKEND_URL}/api/vendor-master/vendor-partner-functions/${editingPartnerFunction.assignment_id}`,
          { partner_number: data.partner_number }
        );
        toast.success('Partner function updated successfully');
      } else {
        // Create new partner function
        await axios.post(`${BACKEND_URL}/api/vendor-master/vendor-partner-functions`, data);
        toast.success('Partner function assigned successfully');
      }
      setIsPartnerDialogOpen(false);
      setEditingPartnerFunction(null);
      setSelectedPartnerFunctionId('');
      loadVendorData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save partner function');
    }
  };

  const handleDeletePartnerFunction = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to remove this partner function assignment?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/vendor-master/vendor-partner-functions/${assignmentId}`);
      toast.success('Partner function removed successfully');
      loadVendorData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove partner function');
    }
  };

  const handleEditPartnerFunction = (vpf) => {
    setEditingPartnerFunction(vpf);
    setSelectedPartnerFunctionId(vpf.partner_function_id);
    setIsPartnerDialogOpen(true);
  };

  const handleOpenAddPartnerDialog = () => {
    setEditingPartnerFunction(null);
    setSelectedPartnerFunctionId('');
    setIsPartnerDialogOpen(true);
  };

  const tabs = [
    { id: 'general', label: 'General Data' },
    { id: 'company', label: 'Company Code' },
    { id: 'purchasing', label: 'Purchasing' },
    { id: 'partner', label: 'Partner Functions' }
  ];

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            <p className="mt-2 text-gray-500">Loading...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {isEditMode && vendorNumber && <InsightCard doctype="vendor" recordId={vendorNumber} />}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? `Edit Vendor: ${vendorNumber}` : 'Create New Vendor'}
            </h1>
            {vendor && (
              <p className="text-sm text-gray-500 mt-1">
                Status: <span className={`font-medium ${vendor.status === 'Active' ? 'text-green-600' : 'text-yellow-600'}`}>
                  {vendor.status}
                </span>
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/vendors')}>
              Cancel
            </Button>
            {isEditMode && vendor?.status === 'Draft' && (
              <Button onClick={handleActivate} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="w-4 h-4 mr-2" />
                Activate
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'general' && (
          <Card className="p-6">
            <form onSubmit={handleSaveDraft}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Account Group *</Label>
                  <select
                    value={selectedAccountGroup}
                    onChange={(e) => handleAccountGroupChange(e.target.value)}
                    disabled={isEditMode}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select Account Group</option>
                    {accountGroups.map(ag => (
                      <option key={ag.account_group_id} value={ag.account_group_id}>
                        {ag.account_group_id} - {ag.description}
                      </option>
                    ))}
                  </select>
                  {selectedAccountGroup && !isEditMode && (
                    isLoadingAccountGroupConfig ? (
                      <p className="text-xs text-gray-500 mt-1">
                        Loading configuration...
                      </p>
                    ) : numberRangeConfig ? (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {numberRangeConfig.range_type === 'EXTERNAL' ? (
                          <>Number Range: {numberRangeConfig.number_range_id} (External - Manual Entry)</>
                        ) : (
                          <>Number Range: {numberRangeConfig.from_number}-{numberRangeConfig.to_number} (Internal - Auto-generated)</>
                        )}
                      </p>
                    ) : (
                      <p className="text-xs text-red-600 mt-1 font-medium">
                        ⚠️ Number Range not configured for this Account Group. Please configure it in Config section before creating vendor.
                      </p>
                    )
                  )}
                </div>

                {isExternalNumbering && !isEditMode && numberRangeConfig && (
                  <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                        <span className="text-amber-600 font-bold">!</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-amber-900 mb-1">
                          External Number Range - Manual Entry Required
                        </h4>
                        <p className="text-xs text-amber-800 mb-3">
                          Number Range: <strong>{numberRangeConfig.number_range_id}</strong> ({numberRangeConfig.description})
                        </p>
                        <div>
                          <Label className="text-amber-900">Vendor Number *</Label>
                          <Input 
                            name="vendor_number" 
                            placeholder={
                              numberRangeConfig.prefix 
                                ? `${numberRangeConfig.prefix}XXXXXXXX (e.g., ${numberRangeConfig.prefix}12345678)`
                                : "Enter 10-character vendor number"
                            }
                            maxLength={10}
                            minLength={10}
                            required
                            className="mt-1 border-amber-300 focus:border-amber-500 focus:ring-amber-500"
                            style={{ textTransform: 'uppercase' }}
                            onChange={(e) => {
                              const value = e.target.value.toUpperCase();
                              e.target.value = value;
                              
                              // Validate format if prefix exists
                              if (numberRangeConfig.prefix && value.length === 10) {
                                if (!value.startsWith(numberRangeConfig.prefix)) {
                                  e.target.setCustomValidity(
                                    `Vendor number must start with prefix "${numberRangeConfig.prefix}"`
                                  );
                                } else if (!/^\d+$/.test(value.substring(numberRangeConfig.prefix.length))) {
                                  e.target.setCustomValidity(
                                    `After prefix "${numberRangeConfig.prefix}", remaining characters must be numeric`
                                  );
                                } else {
                                  e.target.setCustomValidity('');
                                }
                              } else if (value.length === 10 && !/^[A-Z0-9]+$/.test(value)) {
                                e.target.setCustomValidity('Vendor number must contain only letters and numbers');
                              } else {
                                e.target.setCustomValidity('');
                              }
                            }}
                          />
                          <div className="mt-2 text-xs text-amber-700">
                            {numberRangeConfig.prefix ? (
                              <>
                                <div className="font-semibold mb-1">📋 Required Format:</div>
                                <div className="bg-amber-100 border border-amber-300 rounded px-2 py-1 font-mono">
                                  <span className="text-amber-900 font-bold">{numberRangeConfig.prefix}</span>
                                  <span className="text-gray-600"> (prefix) + </span>
                                  <span className="text-amber-900 font-bold">########</span>
                                  <span className="text-gray-600"> (8 digits)</span>
                                </div>
                                <div className="mt-1">
                                  ✓ Total: 10 characters | Example: <strong>{numberRangeConfig.prefix}12345678</strong>
                                </div>
                              </>
                            ) : (
                              <div>✓ Total length must be exactly 10 characters</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {isEditMode && (
                  <div className="col-span-2">
                    <Label>Vendor Number</Label>
                    <Input value={vendor?.vendor_number || ''} disabled className="bg-gray-100" />
                  </div>
                )}

                <div>
                  <Label>Title *</Label>
                  <select name="title" defaultValue={vendor?.title || ''} className="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                    <option value="">Select Title</option>
                    <option value="Company">Company</option>
                    <option value="Mr.">Mr.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Mrs.">Mrs.</option>
                    <option value="Dr.">Dr.</option>
                  </select>
                </div>

                <div>
                  <Label>Name *</Label>
                  <Input name="name" defaultValue={vendor?.name || ''} maxLength={35} required />
                </div>

                <div>
                  <Label>Search Term *</Label>
                  <Input name="search_term" defaultValue={vendor?.search_term || ''} maxLength={10} required style={{ textTransform: 'uppercase' }} />
                  <p className="text-xs text-gray-500 mt-1">Will be converted to uppercase</p>
                </div>

                <div>
                  <Label>Street *</Label>
                  <Input name="street" defaultValue={vendor?.street || ''} maxLength={35} required />
                </div>

                <div>
                  <Label>City *</Label>
                  <Input name="city" defaultValue={vendor?.city || ''} maxLength={35} required />
                </div>

                <div>
                  <Label>Postal Code *</Label>
                  <Input name="postal_code" defaultValue={vendor?.postal_code || ''} maxLength={10} required />
                </div>

                <div>
                  <Label>Region/State *</Label>
                  <Input name="region" defaultValue={vendor?.region || ''} required />
                </div>

                <div>
                  <Label>Country *</Label>
                  <select name="country_key" defaultValue={vendor?.country_key || ''} className="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                    <option value="">Select Country</option>
                    {countries.map(c => (
                      <option key={c.country_code} value={c.country_code}>
                        {c.country_code} - {c.country_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Mobile Number</Label>
                  <Input name="mobile_number" defaultValue={vendor?.mobile_number || ''} type="tel" />
                </div>

                <div>
                  <Label>Language *</Label>
                  <select name="language_key" defaultValue={vendor?.language_key || ''} className="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                    <option value="">Select Language</option>
                    {languages.map(l => (
                      <option key={l.language_code} value={l.language_code}>
                        {l.language_code} - {l.language_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Email</Label>
                  <Input name="email" defaultValue={vendor?.email || ''} type="email" />
                </div>
              </div>

              {/* Frappe-style custom fields (admin-defined per DocType) */}
              <DynamicFieldsSection
                doctype="vendor"
                values={customData}
                onChange={setCustomData}
                title="Additional Fields"
              />

              <div className="flex justify-end gap-2 mt-6 pt-6 border-t">
                <Button type="submit" disabled={isSaving} data-testid="vendor-save-draft-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save Draft'}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {activeTab === 'company' && (
          <Card className="p-6">
            {!isEditMode ? (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="text-gray-600">Company code assignments will be available after saving the vendor.</p>
                <p className="text-sm text-gray-500 mt-2">Please complete the General Data tab and save the vendor first.</p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Company Code Assignments</h3>
                  <Button size="sm" onClick={() => setIsCompanyDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Company Code
                  </Button>
                </div>

            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4">Company Code</th>
                  <th className="text-left py-2 px-4">Reconciliation Account</th>
                  <th className="text-left py-2 px-4">Payment Terms</th>
                  <th className="text-left py-2 px-4">Planning Group</th>
                  <th className="text-right py-2 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendorCompanyCodes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-gray-500">
                      No company codes assigned yet
                    </td>
                  </tr>
                ) : (
                  vendorCompanyCodes.map((vc) => (
                    <tr key={vc.company_code_id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-4">{vc.company_code_id}</td>
                      <td className="py-2 px-4">{vc.reconciliation_account}</td>
                      <td className="py-2 px-4">{vc.payment_terms}</td>
                      <td className="py-2 px-4">{vc.planning_group || '-'}</td>
                      <td className="py-2 px-4 text-right">
                        <Button size="sm" variant="outline" onClick={() => handleDeleteCompanyCode(vc.company_code_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
              </>
            )}
          </Card>
        )}

        {activeTab === 'purchasing' && (
          <Card className="p-6">
            {!isEditMode ? (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <p className="text-gray-600">Purchasing organization assignments will be available after saving the vendor.</p>
                <p className="text-sm text-gray-500 mt-2">Please complete the General Data tab and save the vendor first.</p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Purchasing Organization Assignments</h3>
                  <Button size="sm" onClick={() => setIsPurchasingDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Purchasing Org
                  </Button>
                </div>

            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4">Purchasing Org</th>
                  <th className="text-left py-2 px-4">Currency</th>
                  <th className="text-left py-2 px-4">Purchasing Group</th>
                  <th className="text-left py-2 px-4">Incoterms</th>
                  <th className="text-left py-2 px-4">Payment Terms</th>
                  <th className="text-right py-2 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendorPurchasing.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-gray-500">
                      No purchasing organizations assigned yet
                    </td>
                  </tr>
                ) : (
                  vendorPurchasing.map((vp) => (
                    <tr key={vp.purchasing_org_id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-4">{vp.purchasing_org_id}</td>
                      <td className="py-2 px-4">{vp.order_currency}</td>
                      <td className="py-2 px-4">{vp.purchasing_group_id}</td>
                      <td className="py-2 px-4">{vp.incoterms || '-'}</td>
                      <td className="py-2 px-4">{vp.payment_terms || '-'}</td>
                      <td className="py-2 px-4 text-right">
                        <Button size="sm" variant="outline" onClick={() => handleDeletePurchasing(vp.purchasing_org_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
              </>
            )}
          </Card>
        )}

        {activeTab === 'partner' && (
          <Card className="p-6">
            {!isEditMode ? (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="text-gray-600">Partner function assignments will be available after saving the vendor.</p>
                <p className="text-sm text-gray-500 mt-2">Please complete the General Data tab and save the vendor first.</p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Partner Function Assignments</h3>
              <Button size="sm" onClick={handleOpenAddPartnerDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Partner Function
              </Button>
            </div>

            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4">Partner Function</th>
                  <th className="text-left py-2 px-4">Description</th>
                  <th className="text-left py-2 px-4">Partner Number</th>
                  <th className="text-left py-2 px-4">Partner Vendor Name</th>
                  <th className="text-right py-2 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendorPartnerFunctions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-gray-500">
                      No partner functions assigned yet
                    </td>
                  </tr>
                ) : (
                  vendorPartnerFunctions.map((vpf) => {
                    const pf = allPartnerFunctions.find(p => p.partner_function_id === vpf.partner_function_id);
                    const partnerVendor = availableVendors.find(v => v.vendor_number === vpf.partner_number);
                    return (
                      <tr key={vpf.assignment_id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-4">{vpf.partner_function_id}</td>
                        <td className="py-2 px-4">{pf?.name || '-'}</td>
                        <td className="py-2 px-4">{vpf.partner_number}</td>
                        <td className="py-2 px-4">{partnerVendor?.name || '-'}</td>
                        <td className="py-2 px-4 text-right">
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="outline" onClick={() => handleEditPartnerFunction(vpf)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDeletePartnerFunction(vpf.assignment_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
              </>
            )}
          </Card>
        )}

        {/* Add Company Code Dialog */}
        <Dialog open={isCompanyDialogOpen} onOpenChange={setIsCompanyDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Company Code</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddCompanyCode} className="space-y-4">
              <div>
                <Label>Company Code *</Label>
                <select name="company_code_id" className="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                  <option value="">Select Company Code</option>
                  {companyCodes.map(cc => (
                    <option key={cc.company_code_id} value={cc.company_code_id}>
                      {cc.company_code_id} - {cc.company_code_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Reconciliation Account *</Label>
                <Input name="reconciliation_account" placeholder="e.g., 2100001" maxLength={10} required />
              </div>
              <div>
                <Label>Payment Terms *</Label>
                <select name="payment_terms" className="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                  <option value="">Select Payment Terms</option>
                  {termsOfPayment.map(term => (
                    <option key={term.payment_key} value={term.payment_key}>
                      {term.payment_key} - {term.finance_text || term.sales_text || 'Payment Terms'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Planning Group</Label>
                <Input name="planning_group" placeholder="Planning group for cash management" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsCompanyDialogOpen(false)}>Cancel</Button>
                <Button type="submit">Add</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Purchasing Org Dialog */}
        <Dialog open={isPurchasingDialogOpen} onOpenChange={setIsPurchasingDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Purchasing Organization</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddPurchasing} className="space-y-4">
              <div>
                <Label>Purchasing Organization *</Label>
                <select name="purchasing_org_id" className="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                  <option value="">Select Purchasing Org</option>
                  {purchasingOrgs.map(po => (
                    <option key={po.purchasing_org_id} value={po.purchasing_org_id}>
                      {po.purchasing_org_id} - {po.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Order Currency *</Label>
                <select name="order_currency" className="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                  <option value="">Select Currency</option>
                  {currencies.map(c => (
                    <option key={c.currency_code} value={c.currency_code}>
                      {c.currency_code} - {c.currency_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Purchasing Group *</Label>
                <select name="purchasing_group_id" className="w-full px-3 py-2 border border-gray-300 rounded-md" required>
                  <option value="">Select Purchasing Group</option>
                  {purchasingGroups.map(pg => (
                    <option key={pg.purchasing_group_id} value={pg.purchasing_group_id}>
                      {pg.purchasing_group_id} - {pg.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Incoterms</Label>
                <select name="incoterms" className="w-full px-3 py-2 border border-gray-300 rounded-md">
                  <option value="">Select Incoterms (Optional)</option>
                  {incoterms.map(term => (
                    <option key={term.incoterms_id} value={term.incoterms_id}>
                      {term.incoterms_id} - {term.description}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Payment Terms</Label>
                <select name="payment_terms_purchasing" className="w-full px-3 py-2 border border-gray-300 rounded-md">
                  <option value="">Select Payment Terms (Optional)</option>
                  {termsOfPayment.map(term => (
                    <option key={term.payment_key} value={term.payment_key}>
                      {term.payment_key} - {term.finance_text || term.sales_text || 'Payment Terms'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsPurchasingDialogOpen(false)}>Cancel</Button>
                <Button type="submit">Add</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Partner Function Dialog */}
        <Dialog open={isPartnerDialogOpen} onOpenChange={(open) => {
          setIsPartnerDialogOpen(open);
          if (!open) {
            setEditingPartnerFunction(null);
            setSelectedPartnerFunctionId('');
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingPartnerFunction ? 'Edit Partner Function' : 'Add Partner Function'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddPartnerFunction} className="space-y-4">
              <div>
                <Label>Partner Function *</Label>
                <select 
                  name="partner_function_id" 
                  value={selectedPartnerFunctionId}
                  onChange={(e) => setSelectedPartnerFunctionId(e.target.value)}
                  disabled={!!editingPartnerFunction}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100" 
                  required
                >
                  <option value="">Select Partner Function</option>
                  {allPartnerFunctions.map(pf => {
                    // Check if unique partner function is already consumed (assigned to any vendor)
                    const isConsumed = pf.unique && vendorPartnerFunctions.some(vpf => vpf.partner_function_id === pf.partner_function_id);
                    
                    return (
                      <option 
                        key={pf.partner_function_id} 
                        value={pf.partner_function_id}
                        disabled={isConsumed}
                      >
                        {pf.partner_function_id} - {pf.name}
                        {pf.unique && ' [UNIQUE]'}
                        {isConsumed && ' (Already Consumed)'}
                      </option>
                    );
                  })}
                </select>
                {editingPartnerFunction && (
                  <p className="text-xs text-gray-500 mt-1">Partner Function cannot be changed</p>
                )}
                {selectedPartnerFunctionId && allPartnerFunctions.find(pf => pf.partner_function_id === selectedPartnerFunctionId)?.unique && (
                  <p className="text-xs text-amber-600 mt-1 font-medium">
                    ⚠️ This is a UNIQUE partner function - it can only be assigned once and will be consumed after assignment
                  </p>
                )}
              </div>
              <div>
                <Label>Description</Label>
                <Input 
                  value={allPartnerFunctions.find(pf => pf.partner_function_id === selectedPartnerFunctionId)?.name || '-'}
                  disabled
                  className="bg-gray-50"
                />
                <p className="text-xs text-gray-500 mt-1">Partner function description</p>
              </div>
              <div>
                <Label>Partner Vendor Number *</Label>
                <select 
                  name="partner_number" 
                  defaultValue={editingPartnerFunction?.partner_number || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                  required
                >
                  <option value="">Select Vendor</option>
                  {availableVendors.filter(v => v.status === 'Active').map(v => (
                    <option key={v.vendor_number} value={v.vendor_number}>
                      {v.vendor_number} - {v.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Partner can be same as vendor or different</p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => {
                  setIsPartnerDialogOpen(false);
                  setEditingPartnerFunction(null);
                  setSelectedPartnerFunctionId('');
                }}>Cancel</Button>
                <Button type="submit">{editingPartnerFunction ? 'Update' : 'Add'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        {isEditMode && (
          <div className="mt-6">
            <WorkflowActivity doctype="vendor" recordId={vendorNumber} />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VendorForm;
